'use client';

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { cn } from '@/lib/utils';
import type { Persona } from '@/components/shop/homepage/gpu-characters';

/**
 * Shared art and motion for the GPU arcade. Everything here is decorative:
 * hidden from assistive tech, and still when the visitor prefers reduced
 * motion (the CSS only moves things under `prefers-reduced-motion:
 * no-preference`).
 */

/** Feeds the cursor position into --mx / --my (-1…1) on an element, for parallax layers. */
export function usePointerParallax(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let frame = 0;
    let x = 0;
    let y = 0;
    const update = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0) return;
      const mx = Math.max(-1, Math.min(1, (x - (rect.left + rect.width / 2)) / (rect.width / 2)));
      const my = Math.max(-1, Math.min(1, (y - (rect.top + rect.height / 2)) / (rect.height / 2)));
      el.style.setProperty('--mx', mx.toFixed(3));
      el.style.setProperty('--my', my.toFixed(3));
    };
    const onMove = (event: PointerEvent) => {
      x = event.clientX;
      y = event.clientY;
      if (!frame) frame = requestAnimationFrame(update);
    };
    const onLeave = () => {
      el.style.setProperty('--mx', '0');
      el.style.setProperty('--my', '0');
    };
    el.addEventListener('pointermove', onMove, { passive: true });
    el.addEventListener('pointerleave', onLeave);
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
    };
  }, [ref]);
}

/** Tilt handlers for a card: --tx / --ty follow the cursor inside it. */
export function tiltHandlers() {
  return {
    onPointerMove: (event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerType !== 'mouse') return;
      const el = event.currentTarget;
      const rect = el.getBoundingClientRect();
      el.style.setProperty('--tx', (((event.clientX - rect.left) / rect.width) * 2 - 1).toFixed(3));
      el.style.setProperty('--ty', (((event.clientY - rect.top) / rect.height) * 2 - 1).toFixed(3));
    },
    onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => {
      event.currentTarget.style.setProperty('--tx', '0');
      event.currentTarget.style.setProperty('--ty', '0');
    },
  };
}

/** Fades its content up the first time it scrolls into view. */
export function Reveal({
  children,
  className,
  index = 0,
  id,
}: {
  children: ReactNode;
  className?: string;
  index?: number;
  id?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      id={id}
      data-shown={shown}
      style={{ '--i': index } as CSSProperties}
      className={cn('ga-reveal', className)}
    >
      {children}
    </div>
  );
}

/** A glossy GPU chip with a dark die, metal pins and a glowing core. */
export function GpuChip({ hue, className, label }: { hue: string; className?: string; label?: string }) {
  return (
    <div aria-hidden style={{ '--hue': hue } as CSSProperties} className={cn('relative aspect-square', className)}>
      <div className="ga-chip-pins absolute inset-x-[18%] -top-[9%] h-[12%] rounded-sm" />
      <div className="ga-chip-pins absolute inset-x-[18%] -bottom-[9%] h-[12%] rounded-sm" />
      <div className="ga-chip-pins-y absolute inset-y-[18%] -left-[9%] w-[12%] rounded-sm" />
      <div className="ga-chip-pins-y absolute inset-y-[18%] -right-[9%] w-[12%] rounded-sm" />
      <div className="ga-chip absolute inset-0 rounded-[22%]">
        <div className="ga-chip-die absolute inset-[22%] grid place-items-center rounded-[18%]">
          {label ? (
            <span className="font-mono text-[9px] font-bold tracking-wider text-foreground">{label}</span>
          ) : (
            <span className="power-pulse block h-1/3 w-1/3 rounded-[25%] bg-[var(--hue)] shadow-[0_0_14px_var(--hue)]" />
          )}
        </div>
        <span className="absolute left-[10%] top-[8%] h-[10%] w-[34%] rounded-full bg-foreground/50" />
      </div>
    </div>
  );
}

/** A luminous stage disc that a character stands on, with a light beam rising. */
export function Platform({ hue, className, beam = true }: { hue: string; className?: string; beam?: boolean }) {
  return (
    <div aria-hidden style={{ '--hue': hue } as CSSProperties} className={cn('relative', className)}>
      {beam && <div className="ga-beam absolute bottom-[40%] left-1/2 h-[520%] w-[88%] -translate-x-1/2" />}
      <div className="ga-platform-rim absolute inset-x-0 top-[22%] bottom-0" />
      <div className="ga-platform-top relative h-[78%] w-full" />
    </div>
  );
}

/** Pixels rising from the floor of a stage. [left %, size px, delay s, duration s, hue] */
export type RisingPixel = [number, number, number, number, string];

export function RisingPixels({ pixels, className }: { pixels: RisingPixel[]; className?: string }) {
  return (
    <div aria-hidden className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}>
      {pixels.map(([left, size, delay, duration, hue], i) => (
        <span
          key={i}
          className="pixel-rise absolute bottom-0 rounded-[3px] motion-reduce:hidden"
          style={
            {
              left: `${left}%`,
              width: size,
              height: size,
              background: hue,
              boxShadow: `0 0 ${size * 2}px ${hue}`,
              animationDelay: `-${delay}s`,
              '--rise': `${duration}s`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}

/** A small burst of pixels that pops out of a card on hover. */
export function HoverPixels() {
  const bits: [number, number, string][] = [
    [18, -26, '0s'],
    [36, 14, '0.08s'],
    [58, -10, '0.16s'],
    [76, 22, '0.04s'],
    [88, -18, '0.12s'],
  ];
  return (
    <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-[18%] h-0">
      {bits.map(([left, drift, delay], i) => (
        <span
          key={i}
          className="ga-pop absolute h-2 w-2 rounded-[2px] bg-[var(--hue)] shadow-[0_0_10px_var(--hue)]"
          style={{ left: `${left}%`, '--px': `${drift}px`, '--delay': delay } as CSSProperties}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Voxel objects. Drawn with the same glossy-bead renderer as the GPU
// characters (GpuCharacter), so the objects belong to the same toy family.
// Map keys: b body, l highlight, s shade, p dark screen, e bright, f face
// light, g glow, m metal.
// ---------------------------------------------------------------------------

// A sealed loot crate with a padlock: the GPU nobody can rent right now.
const CRATE = [
  '......mmmm......',
  '.....mm..mm.....',
  '.....m....m.....',
  'sssssmssssmsssss',
  'sbllmmmmmmmmbbbs',
  'sblbmppppppmbbbs',
  'sbbbmpppgppmbbbs',
  'sbbbmpppgppmbbbs',
  'sbbbmppppppmbbbs',
  'sbbbmmmmmmmmbbbs',
  'sbbbbbbbbbbbbbbs',
  'ssssssssssssssss',
  '.ss..........ss.',
] as const;

// A dual-fan graphics card.
const CARD_OBJ = [
  '.bbbbbbbbbbbbbbbb.',
  'mbllbbbbbbbbbbbbbb',
  'mbpppppbbbbpppppbb',
  'mbppfppbbbbppfppbb',
  'mbpfgfpbggbpfgfpbb',
  'mbppfppbbbbppfppbb',
  'mbpppppbbbbpppppbb',
  'mbbbbbbbbbbbbbbbbb',
  '..s.s.s.s.s.s.s...',
] as const;

// A chunky power button.
const POWER_OBJ = [
  '....bbbbbb....',
  '..bblbbbbbbb..',
  '.blbbbbgbbbbb.',
  '.bbbgbbgbbgbb.',
  'bbbgbbbgbbbgbb',
  'bbgbbbbgbbbbgb',
  'bbgbbbbbbbbbgb',
  'bbgbbbbbbbbbgb',
  'bbbgbbbbbbbgbb',
  '.bbbgbbbbbgbb.',
  '.bbbbgggggbbb.',
  '..bbbbbbbbbb..',
  '....ssssss....',
  '..ssssssssss..',
] as const;

// A monitor showing a remote desktop window.
const SCREEN_OBJ = [
  'bbbbbbbbbbbbbbbb',
  'blpppppppppppppb',
  'bpffffffpppppppb',
  'bpfeeeefpppgpppb',
  'bpfeeeefppgggppb',
  'bpfeeeefpppgpppb',
  'bpffffffpppppppb',
  'bppppppppppppppb',
  'bbbbbbbbbbbbbbbb',
  '......mmmm......',
  '.....mmmmmm.....',
  '...ssssssssss...',
] as const;

export const LOCKED_PERSONA: Persona = {
  codename: 'Sealed',
  role: 'Locked',
  tagline: 'Not available to rent right now.',
  hue: 'lavender',
  glow: 'magenta',
  map: CRATE,
};

export const STEP_OBJECTS: Persona[] = [
  { codename: 'Card', role: 'Pick', tagline: '', hue: 'magenta', glow: 'lime', map: CARD_OBJ },
  { codename: 'Power', role: 'Launch', tagline: '', hue: 'ember', glow: 'lime', map: POWER_OBJ },
  { codename: 'Screen', role: 'Connect', tagline: '', hue: 'mint', glow: 'lime', map: SCREEN_OBJ },
];
